import { ArrowSquareOut } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { useMemo } from 'react';
import { HTTPError } from 'src/api/createApiClient.types';
import type { CloudConnectSubscription } from 'src/api/endpoints/cloudConnect.types';
import type { CountryCode } from 'src/api/endpoints/myRmApi.types';
import {
  useCreateBillingPortalSession,
  useEnterpriseSubscription,
} from 'src/api/queries/enterpriseSubscription';
import { useAccessTokenClaims } from 'src/api/queries/user';
import {
  Button,
  Divider,
  FlexDivider,
  NotificationBox,
  Spinner,
} from 'src/components';
import { connectSubscriptionHelpers } from 'src/utils/connectSubscriptionHelpers';
import { findCountry } from 'src/utils/countryList';
import { userHelpers } from 'src/utils/userHelpers';
import { PlanCard } from './PlanCard';

export function Plans({
  subscription,
}: {
  subscription: CloudConnectSubscription;
}) {
  const plans = useMemo(() => {
    return [subscription.planCurrent, ...subscription.planOptions];
  }, [subscription.planCurrent, subscription.planOptions]);

  return (
    <>
      <div>
        <Typography
          variant="interface-xl"
          fontWeight="semibold"
          leadingNone
          as="h2"
        >
          Plan
        </Typography>

        <div className="mt-[45px] flex flex-col gap-32 lg:flex-row">
          {plans.map((plan) => (
            <PlanCard
              plan={plan}
              subscription={subscription}
              key={plan.interval}
            />
          ))}
        </div>

        {subscription.cancelRequested && !subscription.cancelAtPeriodEnd && (
          <NotificationBox
            className="mt-24"
            variant="warning"
            title="Cancel requested"
          >
            Your subscription has a cancellation request in progress and we will
            get in touch to assist you with the next steps. Billing will be
            paused at the end of the current period, but Connect for Business
            features will remain active until the process is complete.
          </NotificationBox>
        )}
      </div>
    </>
  );
}

export function EnterpriseBilling() {
  const subscription = useEnterpriseSubscription();
  const billingPortalSession = useCreateBillingPortalSession();
  const accessTokenClaims = useAccessTokenClaims();
  const hasPermissionsUpdateEnterpriseSubscription =
    accessTokenClaims.data &&
    userHelpers.hasPermissionUpdateSubscription(accessTokenClaims.data);

  const canUpdateSubscription =
    hasPermissionsUpdateEnterpriseSubscription &&
    !billingPortalSession.isPending &&
    !!billingPortalSession.data?.url;

  const addressLine = useMemo(() => {
    if (!subscription.data?.address) {
      return '';
    }

    return [
      subscription.data.address.city,
      subscription.data.address.state,
      subscription.data.address.postalCode,
      findCountry(subscription.data.address.country as CountryCode)?.label ??
        subscription.data.address.country,
    ]
      .filter(Boolean)
      .join(', ');
  }, [subscription.data?.address]);

  if (subscription.isPending) {
    return <Spinner />;
  }

  if (subscription.isError && subscription.error instanceof HTTPError) {
    if (subscription.error?.response.status === 404) {
      return (
        <div className="">
          <Typography variant="heading-subtle-2xl" as="h2">
            Billing not yet active
          </Typography>
          <Typography variant="body-md" className="mt-24 mb-56">
            Billing is not yet enabled for your account, so you won&apos;t be
            charged for your usage.
            <br />
            We&apos;ll let you know as soon as subscriptions become available.
          </Typography>
        </div>
      );
    }
  }

  if (!subscription.data) {
    return (
      <NotificationBox
        variant="info"
        title="No active subscription"
        data-cy="no-subscription-notification"
      >
        No subscription exists for this account.
      </NotificationBox>
    );
  }

  const hasPlans =
    subscription.data.planCurrent && subscription.data.planOptions?.length > 0;

  return (
    <div className="mb-56 flex flex-col gap-y-56">
      {hasPlans && (
        <div className="flex flex-col gap-48">
          <Plans subscription={subscription.data} />
          {subscription.data.planUpcoming && (
            <NotificationBox variant="info" title="Plan change upcoming">
              You have a plan change scheduled to take effect at the end of your
              current billing period. If you have any questions about this
              change, please contact your account manager.
            </NotificationBox>
          )}
          {connectSubscriptionHelpers.isOverdue(subscription.data) && (
            <NotificationBox variant="error" title="Overdue payment detected">
              You&rsquo;ll keep full access until end of billing period. A
              member of our team will reach out to confirm and guide you through
              the offboarding process.
            </NotificationBox>
          )}
          <Divider />
        </div>
      )}
      <div>
        <div className="flex items-start justify-between">
          <Typography variant="body-lg" as="h2" className="font-semibold">
            Billing information
          </Typography>
          {billingPortalSession.data && canUpdateSubscription && (
            <Button
              variant="tertiary"
              size="medium"
              loading={billingPortalSession.isPending}
              disabled={!canUpdateSubscription}
              as="a"
              to={billingPortalSession.data.url}
              target="_blank"
              rel="noopener noreferrer"
              data-cy="edit-billing-button"
            >
              <span>Edit</span>
              <ArrowSquareOut size={10} />
            </Button>
          )}
        </div>
        <div className="mt-24 flex flex-col justify-between gap-16">
          <div className="flex w-full flex-col gap-16 sm:flex-row">
            <div className="flex-1" data-cy="billing-address">
              <Typography variant="body-md" className="font-medium">
                Address
              </Typography>
              <Typography variant="body-md" className="mt-8">
                {subscription.data.name}
              </Typography>
              <Typography variant="body-md">
                {subscription.data.address.line1}
              </Typography>
              <Typography variant="body-md">{addressLine}</Typography>
            </div>

            <FlexDivider className="hidden sm:flex" />

            <div className="flex-1">
              <Typography variant="body-md" className="font-medium">
                Company
              </Typography>
              <Typography variant="body-md" className="mt-8">
                {subscription.data.name}
              </Typography>
              <Typography variant="body-md">
                {subscription.data.taxId || '-'}
              </Typography>
            </div>
          </div>
          <div className="mt-24">
            <Typography variant="body-md" className="font-medium">
              Contact email
            </Typography>
            <Typography variant="body-md" className="mt-8">
              {subscription.data.email}
            </Typography>
          </div>
        </div>

        {billingPortalSession.data && canUpdateSubscription && (
          <Button
            variant="tertiary"
            size="medium"
            loading={billingPortalSession.isPending}
            disabled={!canUpdateSubscription}
            as="a"
            to={billingPortalSession.data.url}
            target="_blank"
            rel="noopener noreferrer"
            data-cy="billing-history-button"
            className="mt-24"
          >
            <span>Billing history</span>
            <ArrowSquareOut size={10} />
          </Button>
        )}
      </div>
    </div>
  );
}
